import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { TaskBoard } from '../../components/developer';
import TaskDetailModal from '../../components/developer/TaskDetailModal';
import type { Task as TaskBoardTask } from '../../components/developer/TaskBoard';
import { DeveloperDashboardService, DeveloperTask } from '../../services/DeveloperDashboardService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tasks-tabpanel-${index}`}
      aria-labelledby={`tasks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Developer Tasks Page
 * Comprehensive task management with multiple views
 */
const DeveloperTasksPage: React.FC = () => {
  console.log('🚀 DeveloperTasksPage MOUNTED - VERSION 3.0 🚀');
  
  const [currentTab, setCurrentTab] = useState(0);
  const [tasks, setTasks] = useState<DeveloperTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DeveloperDashboardService.getTasks();
      setTasks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Convert DeveloperTask to TaskBoardTask
  const convertToTaskBoardTasks = (): TaskBoardTask[] => {
    return tasks.map((task) => ({
      id: task.id,
      title: task.title,
      description: task.description || undefined,
      status: mapTaskStatus(task.status),
      priority: mapTaskPriority(task.priority),
      dueDate: task.dueDate || undefined,
      estimatedHours: task.estimatedHours || undefined,
      loggedHours: task.loggedHours || undefined,
      projectName: task.project,
      assignee: task.assignee ? {
        name: task.assignee.name,
        avatar: task.assignee.avatar || undefined,
      } : undefined,
      comments: task.commentsCount || 0,
      attachments: task.attachmentsCount || 0,
    }));
  };

  const mapTaskStatus = (status: DeveloperTask['status']): 'todo' | 'in_progress' | 'review' | 'done' => {
    const statusMap: Record<DeveloperTask['status'], 'todo' | 'in_progress' | 'review' | 'done'> = {
      'backlog': 'todo',
      'todo': 'todo',
      'in_progress': 'in_progress',
      'in_review': 'review',
      'testing': 'review',
      'done': 'done',
      'cancelled': 'done',
    };
    return statusMap[status] || 'todo';
  };

  const mapTaskPriority = (priority: string): 'low' | 'medium' | 'high' | 'urgent' => {
    const priorityMap: Record<string, 'low' | 'medium' | 'high' | 'urgent'> = {
      'low': 'low',
      'medium': 'medium',
      'high': 'high',
      'urgent': 'urgent',
      'critical': 'urgent',
    };
    return priorityMap[priority.toLowerCase()] || 'medium';
  };

  const handleTaskMove = async (taskId: number, newStatus: 'todo' | 'in_progress' | 'review' | 'done') => {
    console.log('🔥🔥🔥 V3.0 handleTaskMove called', { taskId, newStatus });
    
    // Map Kanban status back to DeveloperTaskStatus
    const statusMap: Record<'todo' | 'in_progress' | 'review' | 'done', DeveloperTask['status']> = {
      'todo': 'todo',
      'in_progress': 'in_progress',
      'review': 'in_review',
      'done': 'done',
    };
    const mappedStatus = statusMap[newStatus];
    
    console.log('📝 Updating task optimistically...');
    
    // Optimistic update
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, status: mappedStatus } : task
    ));
    
    try {
      console.log('📡 Calling API:', `/developer/tasks/${taskId}/status`, { status: mappedStatus });
      await DeveloperDashboardService.updateTaskStatus(taskId, mappedStatus);
      console.log('✅ Task updated successfully!');
    } catch (err: any) {
      console.error('❌ API call failed:', err);
      // Revert on error
      await loadTasks();
    }
  };

  const handleTaskClick = (task: TaskBoardTask) => {
    console.log('handleTaskClick called:', task);
    setSelectedTaskId(task.id);
    setModalOpen(true);
  };

  // Filter tasks by status for different views
  const getActiveTasks = () => tasks.filter(t => 
    ['todo', 'in_progress', 'in_review', 'testing'].includes(t.status)
  );

  const getReviewTasks = () => tasks.filter(t => 
    t.status === 'in_review'
  );

  const getTestingTasks = () => tasks.filter(t => 
    t.status === 'testing'
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Tasks
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage and track your development tasks
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="task views"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Tasks (Kanban)" />
          <Tab label={`Active (${getActiveTasks().length})`} />
          <Tab label={`Code Reviews (${getReviewTasks().length})`} />
          <Tab label={`Testing (${getTestingTasks().length})`} />
          <Tab label="Time Tracking" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop tasks between columns to update their status
          </Typography>
          <TaskBoard
            tasks={convertToTaskBoardTasks()}
            onTaskMove={handleTaskMove}
            onTaskClick={handleTaskClick}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tasks currently in progress or pending
          </Typography>
          <TaskBoard
            tasks={convertToTaskBoardTasks().filter(t => 
              t.status === 'todo' || t.status === 'in_progress' || t.status === 'review'
            )}
            onTaskMove={handleTaskMove}
            onTaskClick={handleTaskClick}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pull requests and code waiting for your review
          </Typography>
          <TaskBoard
            tasks={convertToTaskBoardTasks().filter(t => t.status === 'review')}
            onTaskMove={handleTaskMove}
            onTaskClick={handleTaskClick}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tasks in testing phase
          </Typography>
          {getTestingTasks().length === 0 ? (
            <Alert severity="info">No tasks currently in testing</Alert>
          ) : (
            <TaskBoard
              tasks={convertToTaskBoardTasks().filter(t => 
                tasks.find(task => task.id === t.id)?.status.toLowerCase() === 'testing'
              )}
              onTaskMove={handleTaskMove}
              onTaskClick={handleTaskClick}
            />
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Time Tracking
          </Typography>
          <Alert severity="info">
            Time tracking feature coming soon. You'll be able to log hours and view time reports here.
          </Alert>
          
          {/* TODO: Add time tracking component
          <Box sx={{ mt: 2 }}>
            - Time log table (task, date, hours, description)
            - Weekly summary
            - Time entry form
            - Export timesheet button
          </Box>
          */}
        </TabPanel>
      </Paper>

      <TaskDetailModal 
        open={modalOpen}
        taskId={selectedTaskId}
        onClose={() => setModalOpen(false)}
        onTaskUpdated={loadTasks}
      />
    </Container>
  );
};

export default DeveloperTasksPage;
